import { useLazyQuery } from '@apollo/client';
import cloneDeep from 'lodash/cloneDeep';
import {
  SearchSkills as SearchSkillsData,
  SearchSkills_searchSkills as SkillSuggestion,
  SearchSkillsVariables,
} from '../../../__generated__/types';
import { createStyles, makeStyles, MenuItem, MenuList, Paper, Popper } from '@material-ui/core';
import React, { useEffect, useRef, useState } from 'react';

import TextField, { TextFieldProps } from '../../../_components/forms/TextField';
import { SearchSkills } from '../../../_services/candidates/queries';

interface CandidateSkillTextFieldProps extends Omit<TextFieldProps, 'onChange'> {
  onSelect?: (skill: SkillSuggestion) => void;
  alreadySelectedSkillNames?: string[];
  onChange?: (value: string) => void;
}

const useStyles = makeStyles(() =>
  createStyles({
    container: {
      position: 'relative',
      width: '100%',
    },
    menuContainer: {
      zIndex: 13000,
      width: '100%',
      maxHeight: '200px',
      overflowY: 'auto',
      marginTop: '4px',
      top: 78,
    },
    menuItem: {
      whiteSpace: 'normal',
      wordWrap: 'break-word',
    },
  }),
);

const CandidateSkillTextField: React.FC<CandidateSkillTextFieldProps> = ({
  onSelect,
  onChange,
  value,
  alreadySelectedSkillNames = [],
  ...textFieldProps
}) => {
  const classes = useStyles();
  const [skillSuggestions, setSkillSuggestions] = useState<SkillSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [debounceTimer, setDebounceTimer] = useState<NodeJS.Timeout | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const [searchSkillsQuery] = useLazyQuery<SearchSkillsData, SearchSkillsVariables>(SearchSkills, {
    onCompleted: (data) => {
      if (data?.searchSkills) {
        setSkillSuggestions(cloneDeep(data.searchSkills) as SkillSuggestion[]);
        setShowSuggestions(data.searchSkills.length > 0);
      }
    },
    onError: () => {
      setSkillSuggestions([]);
      setShowSuggestions(false);
    },
  });

  const popperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const isInsideContainer = containerRef.current?.contains(target);
      const isInsidePopper = popperRef.current?.contains(target);

      if (!isInsideContainer && !isInsidePopper) {
        setShowSuggestions(false);
      }
    };

    if (showSuggestions) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSuggestions]);

  useEffect(() => {
    if (!showSuggestions) return;

    const modalBody = document.getElementById('modalBody');
    if (!modalBody) return;

    const handleScroll = () => {
      setShowSuggestions(false);
    };

    modalBody.addEventListener('scroll', handleScroll);
    document.addEventListener('scroll', handleScroll);

    return () => {
      modalBody.removeEventListener('scroll', handleScroll);
      document.removeEventListener('scroll', handleScroll);
    };
  }, [showSuggestions]);

  const handleChange = (newValue: string) => {
    if (onChange) {
      onChange(newValue);
    }

    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }

    if (newValue.trim().length >= 0) {
      const timer = setTimeout(() => {
        searchSkillsQuery({
          variables: { query: newValue.trim(), selected_skills: alreadySelectedSkillNames },
        });
      }, 200);

      setDebounceTimer(timer);
    } else {
      setSkillSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleSuggestionSelect = (suggestion: SkillSuggestion) => {
    setShowSuggestions(false);
    onSelect?.(suggestion);
  };

  return (
    <div ref={containerRef} className={classes.container}>
      <TextField
        {...textFieldProps}
        value={value}
        onChange={handleChange}
        onFocus={() => {
          searchSkillsQuery({
            variables: { query: value?.toString()?.trim() ?? '', selected_skills: alreadySelectedSkillNames },
          });
          setShowSuggestions(true);
        }}
      />
      <Popper
        open={showSuggestions && skillSuggestions.length > 0}
        anchorEl={containerRef.current}
        placement="bottom-start"
        style={{ zIndex: 13000, width: containerRef.current?.offsetWidth }}
        modifiers={{
          flip: {
            enabled: false,
          },
          preventOverflow: {
            enabled: false,
          },
          offset: {
            enabled: true,
            offset: '0, -20',
          },
        }}
      >
        <div ref={popperRef}>
          <Paper className={classes.menuContainer}>
            <MenuList>
              {skillSuggestions.map((suggestion) => (
                <MenuItem
                  key={suggestion.name}
                  onClick={() => handleSuggestionSelect(suggestion)}
                  className={classes.menuItem}
                >
                  {suggestion.name}
                </MenuItem>
              ))}
            </MenuList>
          </Paper>
        </div>
      </Popper>
    </div>
  );
};

export default CandidateSkillTextField;
